import useOrder from 'hooks/Order/useOrder'
import { usePlaceByCoords } from 'hooks/usePlace'
import { useEffect, useMemo } from 'react'
import useCarHireOfferListFilters from './useCarHireOfferListFilters'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getDefaultUserDriverCategory } from 'selectors/carHireSelectors'
import { getCarHireListKey } from 'components/CarHire/carHireUtils'
import { useCarHireSearchLink } from 'checkout/Components/Confirmation/BookingDetailsV2/UpsellTiles/useCarHireSearchLink'
import { dateIsAfter } from 'lib/datetime/dateUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { getAllOrderItems } from 'lib/order/orderUtils'
import { fetchOrderEligibleForCarHireBundle } from 'actions/CarHireActions'

const emptyCarHireList: App.CarHireList = {
  vehicles: [],
  fetching: true,
  bundledOrder: undefined,
  pickUp: { date: '', dateChanged: false },
  dropOff: { date: '', dateChanged: false },
}

const emptyCheapestList: App.CarHireCheapestOfferList = {
  offerSummary: undefined,
  fetching: true,
}

const EMPTY_BUNDLE_ELIGIBILITY: App.CarHireOrderBundleEligibility = {
  isEligible: false,
  benefits: new Set(),
  fetching: false,
}

function useOrderItemsEligibleForCarHireBundle(orderId?: string) {
  const [order, isFetchingOrder] = useOrder(orderId)
  const {
    fetching: isFetchingBundleEligibility,
    ...bundleEligibility
  } = useAppSelector(state => state.carHire.bundleEligibilityOrdersList[orderId ?? '']) ?? EMPTY_BUNDLE_ELIGIBILITY

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchOrderEligibleForCarHireBundle(orderId))
  }, [orderId, dispatch])

  const [defaultAgeCategory, defaultAge] = useAppSelector(getDefaultUserDriverCategory)

  const [place, fetchingPlace] = usePlaceByCoords(bundleEligibility.earliestItem?.latitude, bundleEligibility.earliestItem?.longitude)

  const { pickUpDate, dropOffDate } = useMemo(() => {
    if (!bundleEligibility.earliestItem || !bundleEligibility.latestItem) return { pickUpDate: undefined, dropOffDate: undefined }

    let pickUpDate = bundleEligibility.earliestItem.checkIn
    const dropOffDate = bundleEligibility.latestItem.checkOut
    const now = new Date()

    // If hotel check-in has already passed, use today's date as the pick-up date
    if (dateIsAfter(now, new Date(pickUpDate))) {
      pickUpDate = moment(now).format(ISO_DATE_FORMAT)
    }

    return {
      pickUpDate,
      dropOffDate,
    }
  }, [bundleEligibility.earliestItem, bundleEligibility.latestItem])

  const eligibleItems = useMemo(() => {
    const earliestEligibleItem = bundleEligibility.earliestItem
    const latestEligibleItem = bundleEligibility.latestItem

    if (!order || !earliestEligibleItem || !latestEligibleItem) return { earliestItem: undefined, latestItem: undefined }

    const allItems = getAllOrderItems(order)
    const earliestOrderItem = allItems.find((item) => item.id === earliestEligibleItem.itemId)
    const latestOrderItem = allItems.find((item) => item.id === latestEligibleItem.itemId)

    if (!earliestOrderItem || !latestOrderItem) return { earliestItem: undefined, latestItem: undefined }

    return {
      earliestItem: {
        ...earliestEligibleItem,
        orderItem: earliestOrderItem,
      },
      latestItem: {
        ...latestEligibleItem,
        orderItem: latestOrderItem,
      },
    }
  }, [bundleEligibility.earliestItem, bundleEligibility.latestItem, order])

  const filters = useCarHireOfferListFilters({
    pickUpDate,
    dropOffDate,
    pickUpTime: '10:00',
    dropOffTime: '10:00',
    pickUpLocationId: place?.id,
    dropOffLocationId: place?.id,
    driversAgeCategory: defaultAgeCategory,
    isPickUpAtAirport: false,
    pickUpSearchType: 'geo',
    dropOffSearchType: 'geo',
    driversAge: defaultAge,
    bundledOrderId: orderId,
    pickUpLatitude: bundleEligibility.earliestItem?.latitude,
    pickUpLongitude: bundleEligibility.earliestItem?.longitude,
    dropOffLatitude: bundleEligibility.latestItem?.latitude,
    dropOffLongitude: bundleEligibility.latestItem?.longitude,
  })

  const [searchLink] = useCarHireSearchLink(
    bundleEligibility.earliestItem?.checkIn,
    bundleEligibility.latestItem?.checkOut,
    bundleEligibility.earliestItem?.latitude,
    bundleEligibility.earliestItem?.longitude,
    orderId,
  )

  const listKey = useMemo(() => getCarHireListKey(filters), [filters])

  const {
    vehicles,
    fetching: fetchingVehiclesList,
  } = useAppSelector(state => state.carHire.offerLists[listKey]) ?? emptyCarHireList

  const {
    offerSummary: cheapestVehicle,
    fetching: fetchingCheapestVehicle,
  } = useAppSelector(state => state.carHire.cheapestOfferLists[listKey]) ?? emptyCheapestList

  const hasVehicles = vehicles.length > 0 || !!cheapestVehicle
  const fetchingVehicles = fetchingVehiclesList && fetchingCheapestVehicle

  return {
    ...(hasVehicles || fetchingVehicles ? bundleEligibility : EMPTY_BUNDLE_ELIGIBILITY),
    place,
    fetching: isFetchingOrder || isFetchingBundleEligibility || fetchingPlace || (bundleEligibility.isEligible && fetchingVehicles),
    filters,
    searchLink,
    ...(eligibleItems.earliestItem && eligibleItems.latestItem && {
      earliestItem: eligibleItems.earliestItem,
      latestItem: eligibleItems.latestItem,
    }),
  }
}

export default useOrderItemsEligibleForCarHireBundle
